import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

interface ThemeStore {
  isDarkTheme: boolean;
  toggleDarkTheme: () => void;
}

const DARK_THEME_CLASS = "dark-theme";

// Global (not per-header) so the choice survives navigating between the
// analytics app (AuthHeader) and the builder (BuilderHeader) — each has its
// own toggle button, but they read/write the same state rather than each
// header resetting to light on mount.
//
// Persisted to localStorage so the choice also survives a reload. localStorage
// is synchronous, so the store is hydrated while this module loads — before
// React's first render — which means charts render in the right theme straight
// away instead of rendering light and re-rendering dark. It's one tiny key read
// once at startup and written only on toggle, so it costs the main thread
// nothing noticeable. If storage is unavailable (private mode, blocked site
// data), createJSONStorage falls back to no persistence: the toggle still works
// for the session, it just isn't remembered.
const useThemeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      isDarkTheme: false,
      toggleDarkTheme: () => set((state) => ({ isDarkTheme: !state.isDarkTheme })),
    }),
    {
      name: "theme-preference",
      storage: createJSONStorage(() => localStorage),
      // Only the flag is stored, not the toggle function.
      partialize: (state) => ({ isDarkTheme: state.isDarkTheme }),
    },
  ),
);

// <body>'s dark-theme class (which all the dark styles hang off) follows the
// store from here rather than from a component effect: an effect runs after
// the first paint, so a reload in dark mode would flash light for a frame, and
// pages without a header toggle mounted would never get the class at all.
const applyThemeClass = (isDark: boolean) =>
  document.body.classList.toggle(DARK_THEME_CLASS, isDark);
applyThemeClass(useThemeStore.getState().isDarkTheme);
useThemeStore.subscribe((state, prev) => {
  if (state.isDarkTheme !== prev.isDarkTheme) applyThemeClass(state.isDarkTheme);
});

export default useThemeStore;
